import { useCallback, useEffect, useLayoutEffect, useReducer, useRef, useState } from "react";
import { createFieldApi } from "../utils/createFieldApi.js";
import { useFormInternalContext } from "../utils/formContext.js";
const emptyPlaceholder = {};
function useFormField(fieldName, maybeLazyOptions) {
  const { api: formApi } = useFormInternalContext();
  const [fieldApi, setFieldApi] = useState(() => {
    return createFieldApi(formApi, fieldName);
  });
  useEffect(() => {
    setFieldApi(createFieldApi(formApi, fieldName));
  }, [formApi, fieldName]);
  const rOptionsInitialized = useRef(false);
  const rOptions = useRef(
    rOptionsInitialized.current ? (
      // when already initialized a placeholder empty option object is used
      emptyPlaceholder
    ) : (
      // the first render, when possible, initialize the ref value
      (typeof maybeLazyOptions === "function" ? maybeLazyOptions() : maybeLazyOptions) ?? emptyPlaceholder
    )
  );
  rOptionsInitialized.current = true;
  useLayoutEffect(() => {
    if (typeof maybeLazyOptions === "function") {
      if (Object.keys(rOptions.current).length === 0) {
        rOptions.current = maybeLazyOptions();
      }
    } else {
      rOptions.current = maybeLazyOptions ?? rOptions.current;
    }
  }, [maybeLazyOptions]);
  const { mutation, transform, validations, hidden, disabled, required } = rOptions.current;
  useEffect(() => {
    const { dynamic } = rOptions.current;
    if (!dynamic) return;
    const { clearOnUnmount } = dynamic;
    return () => {
      if (clearOnUnmount === "never") return;
      const completeFieldDestruction = clearOnUnmount === "all";
      formApi.deregisterField(fieldName, completeFieldDestruction);
    };
  }, [formApi, fieldName]);
  const [, forceRender] = useReducer((s) => s + 1, 0);
  const getState = useCallback(() => {
    const { dynamic } = rOptions.current;
    if (dynamic) {
      const { field } = dynamic;
      formApi.registerField(fieldName, field);
    }
    return {
      field: formApi.getField(fieldName),
      errors: formApi.getFieldErrors(fieldName)
    };
  }, [formApi, fieldName]);
  const rState = useRef(getState());
  useLayoutEffect(() => void Object.assign(rState.current, getState()), [getState, fieldName]);
  useEffect(() => {
    let effectCleared = false;
    const { fieldsOfInterest } = rOptions.current;
    const unsubscribe = formApi.subscribeToField(fieldName, (field, errors, _formApi) => {
      if (effectCleared) return;
      const changedFields = /* @__PURE__ */ new Set();
      if (fieldsOfInterest?.length) {
        for (const key of Object.keys(field)) {
          const fieldKey = key;
          if (field[fieldKey] !== rState.current.field[fieldKey]) {
            changedFields.add(fieldKey);
          }
        }
      }
      rState.current.field = field;
      rState.current.errors = errors;
      if (!fieldsOfInterest?.length) {
        forceRender();
        return;
      }
      if (fieldsOfInterest.some((f) => changedFields.has(f))) {
        forceRender();
      }
    });
    return () => {
      effectCleared = true;
      unsubscribe();
    };
  }, [formApi, fieldName]);
  useEffect(() => {
    if (mutation) {
      fieldApi.addMutation(mutation);
    }
    return () => {
      if (mutation) {
        fieldApi.removeMutation();
      }
    };
  }, [fieldApi, mutation]);
  useEffect(() => {
    if (transform) {
      fieldApi.addTransform(transform);
    }
    return () => {
      if (transform) {
        fieldApi.removeTransform();
      }
    };
  }, [fieldApi, transform]);
  useEffect(() => {
    if (hidden !== void 0) {
      fieldApi.setVisible(!hidden);
    }
  }, [fieldApi, hidden]);
  useEffect(() => {
    if (required !== void 0) {
      fieldApi.setRequired(required);
    }
  }, [fieldApi, required]);
  useEffect(() => {
    if (disabled !== void 0) {
      fieldApi.setDisabled(disabled);
    }
  }, [fieldApi, disabled]);
  useEffect(() => {
    if (validations) {
      for (const [validationID, validator] of Object.entries(validations)) {
        fieldApi.addValidation(validationID, validator);
      }
    }
    return () => {
      if (validations) {
        for (const validationID of Object.keys(validations)) {
          fieldApi.removeValidation(validationID);
        }
      }
    };
  }, [fieldApi, validations]);
  return { field: rState.current.field, errors: rState.current.errors, fieldApi, formApi };
}
export {
  useFormField
};
//# sourceMappingURL=useFormField.js.map
